import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import '../styles/HotelRooms.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function HotelRooms() {
  const [hotels, setHotels] = useState([]);
  const [displayHotels, setDisplayHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [city, setCity] = useState('');
  const [inputPlace, setInputPlace] = useState('');

  const { loggedIn, fetchWithAuth, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const HOTELS_PER_BATCH = 4;

  // Function to get coordinates from place name
  const getCoordsFromPlace = async (place) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(place)}&limit=1`
      );
      const data = await res.json();
      if (!data || data.length === 0) return null;
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    } catch {
      return null;
    }
  };

  // Get city from coordinates
  const getCityFromCoords = async (lat, lon) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      const data = await res.json();
      return (
        data.address.city ||
        data.address.town ||
        data.address.village ||
        data.address.county ||
        ''
      );
    } catch {
      return '';
    }
  };

  // Fetch hotels by coordinates
  const fetchHotelsByCoords = async (coords) => {
    if (!coords) {
      setError('Invalid place');
      setLoading(false);
      return;
    }

    try {
      const cityName = inputPlace || (await getCityFromCoords(coords.lat, coords.lon));
      setCity(cityName);

      const res = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL}/nearby-hotels?lat=${coords.lat}&lon=${coords.lon}&radius=20000`
      );
      if (!res.ok) throw new Error('Failed to fetch hotels');

      const data = await res.json();
      setHotels(data.hotels);
      setDisplayHotels(data.hotels.slice(0, HOTELS_PER_BATCH));
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch hotels');
    } finally {
      setLoading(false);
    }
  };

  // Handle user place submit
  const handlePlaceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const coords = await getCoordsFromPlace(inputPlace);
    fetchHotelsByCoords(coords);
  };

  // Infinite scroll
  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >=
      document.documentElement.scrollHeight
    ) {
      loadNextHotels();
    }
  };

  const loadNextHotels = () => {
    const currentCount = displayHotels.length;
    const nextBatch = hotels.slice(currentCount, currentCount + HOTELS_PER_BATCH);
    setDisplayHotels([...displayHotels, ...nextBatch]);
  };

  // Initial load: geolocation if user didn't enter a place
  useEffect(() => {
    if (!authLoading && !loggedIn) {
      navigate('/login');
      return;
    }

    if (!inputPlace) {
      const fetchHotels = async () => {
        try {
          let location = null;
          const cachedLocation = sessionStorage.getItem('userLocation');
          const cachedTime = sessionStorage.getItem('locationTimestamp');

          if (cachedLocation && cachedTime && Date.now() - cachedTime < 5 * 60 * 1000) {
            location = JSON.parse(cachedLocation);
          } else if (navigator.geolocation) {
            location = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                (pos) =>
                  resolve({
                    lat: pos.coords.latitude,
                    lon: pos.coords.longitude
                  }),
                (err) => reject(err)
              );
            });
            sessionStorage.setItem('userLocation', JSON.stringify(location));
            sessionStorage.setItem('locationTimestamp', Date.now());
          } else {
            throw new Error('Geolocation not supported.');
          }

          fetchHotelsByCoords(location);
        } catch (err) {
          console.error(err);
          setError(err.message || 'Failed to fetch hotels');
          setLoading(false);
        }
      };

      fetchHotels();
    }
  }, [loggedIn, fetchWithAuth, navigate, inputPlace]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayHotels, hotels]);

  const handleBookHotel = () => {
    if (!city) return alert('Fetching your location...');
    const url = `https://www.justdial.com/${city}/Hotels`;
    window.open(url, '_blank');
  };

  return (
    <div className="hotel-rooms-container">
      <Navbar />
      <div className="hotels-container">
        <h2>Hotels Near You</h2>

        {/* Input for user place */}
        <form onSubmit={handlePlaceSubmit} className="hotel-search-form">
          <input
            type="text"
            value={inputPlace}
            placeholder="Enter city or place"
            onChange={(e) => setInputPlace(e.target.value)}
            className="hotel-input-search"
          />
          <button type="submit" className="hotel-button-search">
            Search
          </button>
        </form>


        <button
          onClick={handleBookHotel}
          style={{
            padding: '10px 20px',
            backgroundColor: '#009dff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          Book a Hotel
        </button>

        {loading && (
          <div className="loading-destinations">
            <div className="loading-spinner"></div>
            <p>Finding best hotels for you...</p>
          </div>
        )}

        {error && <p className="error">{error}</p>}
        {!loading && displayHotels.length === 0 && <p>No hotels found nearby.</p>}

        <div className="hotel-cards">
          {displayHotels.map((hotel, index) => (
            <div className="hotel-card" key={index}>
              <h3>{hotel.name}</h3>
              <p>
                <strong>Address:</strong> {hotel.address}
              </p>
              <p>
                <strong>Rating:</strong> {hotel.rating !== 'N/A' ? hotel.rating : 1}/3⭐
              </p>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
